// store/features/action-columns.feature.ts
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';

export interface ActionColumn {
  id: string;
  lead_list_id: string;
  user_id: string;
  action_type: string;
  tool: string;
  config: Record<string, unknown>;
  display_name: string;
  column_order: number;
  is_active: boolean;
  created_at: string;
  modified_at: string;
}

export interface ActionColumnResult {
  id: string;
  action_column_id: string;
  lead_id: string;
  status: 'pending' | 'running' | 'success' | 'failed' | 'skipped';
  result?: Record<string, unknown>;
  error_message?: string;
  executed_at?: string;
  created_at: string;
  modified_at: string;
}

export interface CreateActionColumnRequest {
  lead_list_id: string;
  action_type: string;
  tool: string;
  config?: Record<string, unknown>;
  display_name: string;
  column_order?: number;
}

export interface UpdateActionColumnRequest {
  display_name?: string;
  config?: Record<string, unknown>;
  column_order?: number;
  is_active?: boolean;
}

export interface ActionColumnProgress {
  action_column_id: string;
  total: number;
  processed: number;
  failed: number;
  percentage: number;
}

export const actionColumnsApi = createApi({
  reducerPath: 'actionColumnsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['ActionColumns', 'ActionColumnResults'],
  endpoints: (builder) => ({
    getActionColumns: builder.query<ActionColumn[], string>({
      query: (leadListId) => `/action-columns?lead_list_id=${leadListId}`,
      providesTags: (_, __, leadListId) => [
        { type: 'ActionColumns', id: leadListId },
      ],
    }),

    createActionColumn: builder.mutation<
      ActionColumn,
      CreateActionColumnRequest
    >({
      query: (body) => ({
        url: '/action-columns',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, __, { lead_list_id }) => [
        { type: 'ActionColumns', id: lead_list_id },
      ],
    }),

    updateActionColumn: builder.mutation<
      ActionColumn,
      { id: string; body: UpdateActionColumnRequest; leadListId: string }
    >({
      query: ({ id, body }) => ({
        url: `/action-columns/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_, __, { leadListId }) => [
        { type: 'ActionColumns', id: leadListId },
      ],
    }),

    deleteActionColumn: builder.mutation<
      void,
      { id: string; leadListId: string }
    >({
      query: ({ id }) => ({
        url: `/action-columns/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { leadListId }) => [
        { type: 'ActionColumns', id: leadListId },
      ],
    }),

    getActionColumnResults: builder.query<ActionColumnResult[], string>({
      query: (columnId) => `/action-columns/${columnId}/results`,
      providesTags: (_, __, columnId) => [
        { type: 'ActionColumnResults', id: columnId },
      ],
    }),

    runActionColumn: builder.mutation<
      {
        status: string;
        action_column_id: string;
        progress: ActionColumnProgress;
      },
      { id: string; leadIds?: string[]; force?: boolean }
    >({
      query: ({ id, leadIds, force }) => ({
        url: `/action-columns/${id}/run`,
        method: 'POST',
        body: {
          ...(leadIds ? { lead_ids: leadIds } : {}),
          ...(force ? { force: true } : {}),
        },
      }),
      invalidatesTags: (result) =>
        result
          ? [{ type: 'ActionColumnResults', id: result.action_column_id }]
          : [],
    }),

    retryFailed: builder.mutation<
      { status: string; action_column_id: string; retry_count: number },
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/action-columns/${id}/retry-failed`,
        method: 'POST',
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'ActionColumnResults', id }],
    }),

    getActionColumnProgress: builder.query<ActionColumnProgress, string>({
      query: (columnId) => `/action-columns/${columnId}/progress`,
    }),
  }),
});

export const {
  useGetActionColumnsQuery,
  useCreateActionColumnMutation,
  useUpdateActionColumnMutation,
  useDeleteActionColumnMutation,
  useLazyGetActionColumnResultsQuery,
  useRunActionColumnMutation,
  useRetryFailedMutation,
  useGetActionColumnProgressQuery,
} = actionColumnsApi;
